import { useAlert } from 'react-alert';
import { Redirect } from 'react-router-dom';
import I18n from 'i18n-js';

import { requestPasswordResetToken } from '@lootboy/lib/actions/AuthActions';
import {
  EVerificationCodeField,
  useVerificationCodeForm,
} from '@lootboy/lib/forms/useVerifivationCodeForm';

import Alert from '~/components/Alert';
import { CodeInput } from '~/components/atoms/FormControls/CodeInput';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import { MenuViewHeader } from '~/components/MenuViewHeader';
import { AuthViewHeadline } from '~/components/molecules/AuthViewHeadline';

import { useAppDispatch } from '~/redux/store/useAppDispatch';
import {
  PASSWORD_RESET_CODE_PATH,
  PASSWORD_RESET_PATH,
  REQUEST_PASSWORD_RESET_PATH,
} from '~/routes/paths';
import { useAppHistory, useAppLocation } from '~/utilities/routing';

import styles from './PasswordResetCode.module.scss';

const PASSWORD_RESET_CODE_FORM_ID = 'password_reset_code_form';

export function PasswordResetCodePopup(): JSX.Element {
  const alert = useAlert();
  const dispatch = useAppDispatch();
  const { state } = useAppLocation<typeof PASSWORD_RESET_CODE_PATH>();
  const history = useAppHistory();

  const email = state?.email;

  const {
    formState: { errors },
    onFormSubmit,
    control,
    isSubmitEnabled,
  } = useVerificationCodeForm(async (value) => {
    if (!email) {
      return;
    }

    try {
      const res = await dispatch(requestPasswordResetToken(email, value.code));
      history.push(PASSWORD_RESET_PATH, { token: res?.token });
    } catch (error) {
      alert.error(
        <Alert
          body={(error as { message?: string })?.message ?? ''}
          title={I18n.t('ResetPasswordView_reset_failed')}
        />,
      );
    }
  });

  if (!email) {
    return <Redirect to={REQUEST_PASSWORD_RESET_PATH} />;
  }

  return (
    <>
      <MenuViewHeader
        title={I18n.t('MenuView_login_bc')}
        onBack={() => {
          history.push(REQUEST_PASSWORD_RESET_PATH);
        }}
      />
      <section className={styles.container}>
        <AuthViewHeadline
          headline={I18n.t('MenuView_reset_code_headline')}
          subHeadline={I18n.t('MenuView_reset_code_sub')}
        />
        <form
          onSubmit={onFormSubmit}
          className={styles.form}
          id={PASSWORD_RESET_CODE_FORM_ID}
        >
          <CodeInput
            label={I18n.t('MenuView_reset_enter_code')}
            id={EVerificationCodeField.CODE}
            control={control}
            name={EVerificationCodeField.CODE}
            error={errors[EVerificationCodeField.CODE]?.message}
          />
        </form>
      </section>
      <footer className={styles.footer}>
        <StyledButton
          variant={EStyledButtonVariant.BLUE}
          type="submit"
          disabled={!isSubmitEnabled}
          className={styles.footerButton}
          form={PASSWORD_RESET_CODE_FORM_ID}
        >
          {I18n.t('confirm')}
        </StyledButton>
      </footer>
    </>
  );
}
